import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { EDIT_DRAFT_ALLOCATION_KEY } from '~/services/localStorage/allocations';
import { useStoreActions, useStoreState } from '~/store/hooks';
import Loader from '~/ui/components/common/Loader';
import { NETWORK } from '~/ui/constants/paths';
import { NETWORK_TAB_SLUGS } from '~/ui/pages/Network/constants';
import useEditAllocationAvailability from './hooks/validateAllocations/useEditAllocationAvailability';

import { AllocationMode } from '../constants/allocationMode';
import useFormStorage from '../hooks/useFormStorage';
import AllocationMembersList from '../shared/components/AllocationMembersList/AllocationMembersList';
import EditAllocationFormActions from '../shared/components/EditAllocationFormActions/EditAllocationFormActions';
import EditAllocationFormLayout from '../shared/components/EditAllocationFormLayout/EditAllocationFormLayout';
import {
  EDIT_PAST_ALLOCATION_DESCRIPTION,
  LEAVE_PAGE_DESCRIPTION,
} from '../shared/constants/allocationMessages';
import useAccordionPanel from '../shared/hooks/useAccordionPanel';
import useAllocationCancelNavigate from '../shared/hooks/useAllocationCancelNavigate';
import usePastDateSubmitGuard from '../shared/hooks/usePastDateSubmitGuard';
import { IEditAllocationFormValues } from '../shared/model/types';
import AssignedClients from './components/AssignedClients';
import { useEditClientAllocationContext } from './context/EditClientAllocationContext';
import useEditAllocationSubmit from './hooks/updateAllocations/useEditAllocationSubmit';
import useEditCrisisAllocationSubmit from './hooks/updateAllocations/useEditCrisisAllocationSubmit';
import useEditLocationGroupAllocationSubmit from './hooks/updateAllocations/useEditLocationGroupAllocationSubmit';

const DEFAULT_VALUES: IEditAllocationFormValues = {
  date: new Date().toString(),
  teamMembersAllocations: [],
  changedAllocationsIds: [],
  deletedAllocationsIds: [],
  changedCrisisAllocationsIds: [],
  deletedCrisisAllocationsIds: [],
  changedLocationGroupAllocationsIds: [],
  deletedLocationGroupAllocationsIds: [],
};

const EditClientAllocationForm = (): ReactElement => {
  const {
    allocations,
    teamMembers,
    actTeamId,
    allocationDate,
    isLoading,
    clinicId,
    allocationMode,
    normalizedAllocationsByUser,
  } = useEditClientAllocationContext();

  const { resetClientAllocationAvailability } = useStoreActions(
    actions => actions.clientAllocation,
  );
  const navigate = useNavigate();
  const { showNotify } = useStoreActions(actions => actions.snackbar);
  const { dateAvailability } = useStoreState(state => state.clientAllocation);

  const initialFormValues = useMemo<IEditAllocationFormValues>(
    () => ({
      date: allocationDate,
      teamMembersAllocations: normalizedAllocationsByUser,
      changedAllocationsIds: [],
      deletedAllocationsIds: [],
      changedCrisisAllocationsIds: [],
      deletedCrisisAllocationsIds: [],
      changedLocationGroupAllocationsIds: [],
      deletedLocationGroupAllocationsIds: [],
    }),
    [allocationDate, normalizedAllocationsByUser],
  );

  const form = useForm<IEditAllocationFormValues>({
    defaultValues: DEFAULT_VALUES,
    values: initialFormValues,
  });

  const {
    formState: { errors, isDirty },
    watch,
    handleSubmit,
    setValue,
  } = form;
  const teamMembersAllocations = watch('teamMembersAllocations');

  useEffect(() => {
    resetClientAllocationAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setValue('teamMembersAllocations', normalizedAllocationsByUser);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedAllocationsByUser]);

  useEditAllocationAvailability({
    teamMembersAllocations,
    allocationDate,
    clinicId,
  });

  const { isRestored, clearDraft } = useFormStorage(
    form,
    {
      key: EDIT_DRAFT_ALLOCATION_KEY,
      conditionKey: 'date',
    },
    initialFormValues,
  );

  const { expandedPanelIds, handleToggle } = useAccordionPanel();

  const { handleCancel, withClearDraft } = useAllocationCancelNavigate({
    actTeamId,
    allocationDate,
  });

  const handleSubmitSuccess = useCallback(() => {
    clearDraft();
    navigate(`${NETWORK}?tab=${NETWORK_TAB_SLUGS.MANAGE_ALLOCATION}`);
  }, [clearDraft, navigate]);

  const { submit: submitAllocation, isSubmitting: isSubmittingAllocation } =
    useEditAllocationSubmit();
  const { submit: submitCrisisAllocation, isSubmitting: isSubmittingCrisis } =
    useEditCrisisAllocationSubmit();
  const { submit: submitLocationGroupAllocation, isSubmitting: isSubmittingLocationGroups } =
    useEditLocationGroupAllocationSubmit();

  const combinedSubmit = useCallback(
    async (values: IEditAllocationFormValues) => {
      const [regularSuccess, crisisSuccess, locationGroupSuccess] = await Promise.all([
        submitAllocation(values),
        submitCrisisAllocation(values),
        submitLocationGroupAllocation(values),
      ]);

      if (regularSuccess || crisisSuccess || locationGroupSuccess) {
        showNotify({ message: 'Client allocation successfully updated' });
        handleSubmitSuccess();
      }
    },
    [
      handleSubmitSuccess,
      showNotify,
      submitAllocation,
      submitCrisisAllocation,
      submitLocationGroupAllocation,
    ],
  );

  const { submitWithGuard, pendingValues, clearPending, confirmPending } =
    usePastDateSubmitGuard<IEditAllocationFormValues>(combinedSubmit);

  const regularAllocations = allocations.filter(
    allocation => !allocation.isCrisis && !allocation.locationGroups.length,
  );
  const hasAllRegularResolved =
    regularAllocations.length > 0
      ? regularAllocations.every(allocation => !!allocation.resolved)
      : false;

  const hasDateErrors =
    dateAvailability &&
    !!dateAvailability?.clients.length &&
    dateAvailability.clients.some(client => !client.canBeCreated);

  const hasAllocationConflict = teamMembersAllocations.some(member => {
    const lgClientIdSet = new Set(
      Object.keys(member.selectedLocationGroupClientIds ?? {}).map(Number),
    );
    return (member.selectedClientsIds ?? []).some(id => lgClientIdSet.has(id));
  });

  const isAnySubmitting =
    isSubmittingAllocation || isSubmittingCrisis || isSubmittingLocationGroups;

  const isSaveDisabled =
    Object.keys(errors).length > 0 ||
    hasAllRegularResolved ||
    hasDateErrors ||
    hasAllocationConflict ||
    !isDirty ||
    isAnySubmitting;

  const isLocationGroupsMode = allocationMode === AllocationMode.BY_LOCATION_GROUP;

  if (!clinicId) {
    return null;
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(submitWithGuard)}>
        <EditAllocationFormLayout
          title={
            isLocationGroupsMode
              ? 'Assigned Location Groups per Team Member'
              : 'Assigned Clients per Team Member'
          }
          isRestored={isRestored}
        >
          <AllocationMembersList
            teamMembersAllocations={teamMembersAllocations}
            clinicId={clinicId}
            expandedPanelIds={expandedPanelIds}
            isLocationGroupsMode={isLocationGroupsMode}
            onToggle={handleToggle}
            renderMemberContent={teamMemberIdx => (
              <AssignedClients
                teamMemberIdx={teamMemberIdx}
                teamMember={teamMembers[teamMemberIdx]}
              />
            )}
          />
        </EditAllocationFormLayout>

        <EditAllocationFormActions
          onCancel={handleCancel}
          isSaveDisabled={isSaveDisabled}
          leavePageWhen={isDirty && !isAnySubmitting}
          leavePageDescription={LEAVE_PAGE_DESCRIPTION}
          withClearDraft={withClearDraft}
          onClearDraft={clearDraft}
          pastDateModal={
            pendingValues
              ? {
                  description: EDIT_PAST_ALLOCATION_DESCRIPTION,
                  onConfirm: confirmPending,
                  onClose: clearPending,
                }
              : null
          }
        />
      </form>
    </FormProvider>
  );
};

export default EditClientAllocationForm;
